import { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { SubscriptionBanner } from '@/components/SubscriptionBanner'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'
import { PageLoader } from '@/components/common/Loaders'
import { AssistantButton } from '@/components/assistant/AssistantButton'
import { AssistantPanelSlot } from '@/components/assistant/AssistantPanelSlot'
import { AssistantProvider } from '@/components/assistant/AssistantProvider'
import { useAuth } from '@/providers/auth'
import { useChatNotifications } from '@/providers/chatNotifications'
import { AppTour } from './AppTour'
import { Breadcrumbs } from './Breadcrumbs'
import { CompanySwitcher } from './CompanySwitcher'
import { GlobalSearch } from './GlobalSearch'
import { NextStepsCard } from './NextStepsCard'
import { MissionCelebration } from '@/components/nextSteps/MissionCelebration'
import { OnboardingCard } from './OnboardingCard'
import { SidebarNav } from './SidebarNav'
import { UserMenu } from './UserMenu'

const SIDEBAR_STORAGE_KEY = '@Habitar:sidebar'

function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'collapsed'
    } catch {
      return false
    }
  })

  const toggle = useCallback(() => {
    setCollapsed(current => {
      const next = !current
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? 'collapsed' : 'expanded')
      } catch {
        return next
      }
      return next
    })
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== 'b' || event.defaultPrevented) return
      if (event.target instanceof HTMLElement && event.target.closest('input, textarea, select, [contenteditable="true"]')) return
      event.preventDefault()
      toggle()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [toggle])

  return { collapsed, toggle }
}

function shortcutHint(): string {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent)
  return mac ? '⌘ B' : 'Ctrl + B'
}

function SidebarContent({ collapsed, inSheet, onNavigate }: { collapsed?: boolean; inSheet?: boolean; onNavigate?: () => void }) {
  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className={cn('flex h-14 shrink-0 items-center px-3', collapsed && 'justify-center', inSheet && 'pr-12')}>
        <CompanySwitcher collapsed={collapsed} onNavigate={onNavigate} />
      </div>
      <SidebarNav collapsed={collapsed} onNavigate={onNavigate} />
      <OnboardingCard collapsed={collapsed} onNavigate={onNavigate} />
      <NextStepsCard collapsed={collapsed} onNavigate={onNavigate} />
      <div className={cn('shrink-0 p-3 pt-1', collapsed && 'flex justify-center')}>
        <UserMenu collapsed={collapsed} onNavigate={onNavigate} />
      </div>
    </div>
  )
}

function SidebarToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const label = collapsed ? 'Expandir menu' : 'Recolher menu'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          aria-label={label}
          aria-expanded={!collapsed}
          aria-controls="app-sidebar"
          className="hidden shrink-0 md:inline-flex"
        >
          {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="flex items-center gap-2 text-xs">
        {label}
        <kbd className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">{shortcutHint()}</kbd>
      </TooltipContent>
    </Tooltip>
  )
}

function MobileMenu() {
  const [open, setOpen] = useState(false)
  const { total: chatUnread } = useChatNotifications()
  const close = () => setOpen(false)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative md:hidden"
          aria-label={chatUnread > 0 ? `Abrir menu (${chatUnread} conversas com mensagens novas)` : 'Abrir menu'}
        >
          <Menu className="h-5 w-5" />
          {chatUnread > 0 && <span aria-hidden className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-destructive ring-2 ring-background" />}
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="flex w-72 flex-col gap-0 border-sidebar-border bg-sidebar p-0">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <SheetDescription className="sr-only">Navegação principal do Habitar</SheetDescription>
        <SidebarContent inSheet onNavigate={close} />
      </SheetContent>
    </Sheet>
  )
}

export function PageContainer({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-full w-full max-w-[1320px] flex-col px-4 py-6 has-[>[data-full-bleed]]:max-w-none has-[>[data-fill-height]]:h-full sm:px-6">
      {children}
    </div>
  )
}

function useScrollTopOnNavigate(pathname: string) {
  const ref = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    ref.current?.scrollTo(0, 0)
  }, [pathname])
  return ref
}

export function AppLayout() {
  const { collapsed, toggle } = useSidebarCollapsed()
  const { company } = useAuth()
  const { pathname } = useLocation()
  const mainRef = useScrollTopOnNavigate(pathname)

  return (
    <TooltipProvider delayDuration={200}>
      <AssistantProvider>
        {!!company.id && <AppTour />}
        <div className="flex h-screen overflow-clip bg-sidebar supports-[height:100dvh]:h-dvh">
          <aside
            data-print-hide
            id="app-sidebar"
            className={cn('hidden h-full shrink-0 overflow-hidden transition-[width] duration-200 ease-out md:block', collapsed ? 'w-16' : 'w-60')}
          >
            <SidebarContent collapsed={collapsed} />
          </aside>

          <div className="flex min-w-0 flex-1 flex-col md:py-2 md:pr-2">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background md:rounded-xl md:border md:border-sidebar-border md:shadow-sm">
              <header data-print-hide className="z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border px-3 sm:px-4">
                <MobileMenu />
                <SidebarToggle collapsed={collapsed} onToggle={toggle} />
                <span aria-hidden className="mx-1 hidden h-5 w-px bg-border md:block" />
                <Breadcrumbs />
                <div className="ml-auto flex shrink-0 items-center gap-1">
                  {!!company.id && <GlobalSearch />}
                  <AssistantButton />
                </div>
              </header>

              <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto">
                <PageContainer>
                  {!!company.id && <SubscriptionBanner />}
                  <AppErrorBoundary resetKey={pathname}>
                    <Suspense fallback={<PageLoader />}>
                      <Outlet />
                    </Suspense>
                  </AppErrorBoundary>
                </PageContainer>
              </main>
            </div>
          </div>

          <AssistantPanelSlot />
        </div>
        {!!company.id && <MissionCelebration />}
      </AssistantProvider>
    </TooltipProvider>
  )
}
